import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/**
 * Shared apartment-filter state for every Tier 1 dashboard page.
 *
 * Semantics mirror the original inline filter logic in `pages/overview.tsx`:
 *   - `bedrooms`:              0 = Any, 1 = one-bed, 2 = 2+ beds.
 *   - `municipalitySectionId`: 0 = Any, otherwise an exact match.
 *   - `ownerGroupId`:          0 = Any, otherwise an exact match.
 *   - `groupId`:               0 = Any, otherwise an exact match on the
 *                              apartment's own group (a portfolio grouping,
 *                              distinct from `ownerGroupId`).
 *   - `tagIds`:                [] = Any, otherwise an apartment matches if it
 *                              has AT LEAST ONE of the selected tag IDs.
 *   - `apartmentIds`:          [] = Any, otherwise an apartment matches iff its
 *                              own ID is one of the selected ones.
 *
 * Combination across the dimensions is AND.
 *
 * Persistence matches the `userSettingsSlice` pattern: each setter writes its
 * value to `localStorage` immediately; `initialState` is hydrated on module
 * load via `loadFromLocalStorage`. Malformed values fall back to defaults
 * without throwing.
 */
export interface ApartmentFilters {
    bedrooms: number;
    municipalitySectionId: number;
    ownerGroupId: number;
    groupId: number;
    tagIds: number[];
    apartmentIds: number[];
    // When false (default) the apartment list hides apartments that are
    // decommissioned as of today (`dateRemoved != null && dateRemoved <= today`).
    // When true, all apartments are shown. Future-dated removals are NEVER
    // hidden regardless of this flag (they are still active now — AS-7).
    showDecommissioned: boolean;
}

const BEDROOMS_KEY = "apartmentFilters.bedrooms";
const MUNICIPALITY_KEY = "apartmentFilters.municipalitySectionId";
const OWNER_GROUP_KEY = "apartmentFilters.ownerGroupId";
const GROUP_KEY = "apartmentFilters.groupId";
const TAG_IDS_KEY = "apartmentFilters.tagIds";
const APARTMENT_IDS_KEY = "apartmentFilters.apartmentIds";
const SHOW_DECOMMISSIONED_KEY = "apartmentFilters.showDecommissioned";

const parseIntOrDefault = (raw: string | null, fallback: number): number => {
    if (raw === null) {
        return fallback;
    }
    const parsed = parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const parseNumberIdsOrDefault = (raw: string | null): number[] => {
    if (raw === null) {
        return [];
    }
    try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) {
            return [];
        }
        const numeric = parsed.filter((value): value is number => typeof value === "number" && Number.isFinite(value));
        return numeric;
    } catch {
        return [];
    }
};

/**
 * Hydrates the slice's initial state from localStorage. Exposed for tests —
 * the slice itself calls it once at module load, so tests that want to assert
 * "hydration with seeded localStorage" exercise this function directly rather
 * than wrestling with module re-evaluation in the browser-mode harness.
 */
export const loadFromLocalStorage = (): ApartmentFilters => ({
    bedrooms: parseIntOrDefault(localStorage.getItem(BEDROOMS_KEY), 0),
    municipalitySectionId: parseIntOrDefault(localStorage.getItem(MUNICIPALITY_KEY), 0),
    ownerGroupId: parseIntOrDefault(localStorage.getItem(OWNER_GROUP_KEY), 0),
    groupId: parseIntOrDefault(localStorage.getItem(GROUP_KEY), 0),
    tagIds: parseNumberIdsOrDefault(localStorage.getItem(TAG_IDS_KEY)),
    apartmentIds: parseNumberIdsOrDefault(localStorage.getItem(APARTMENT_IDS_KEY)),
    showDecommissioned: localStorage.getItem(SHOW_DECOMMISSIONED_KEY) === "true",
});

const defaultFilters = (): ApartmentFilters => ({
    bedrooms: 0,
    municipalitySectionId: 0,
    ownerGroupId: 0,
    groupId: 0,
    tagIds: [],
    apartmentIds: [],
    showDecommissioned: false,
});

const clearPersistedFilters = (): void => {
    localStorage.removeItem(BEDROOMS_KEY);
    localStorage.removeItem(MUNICIPALITY_KEY);
    localStorage.removeItem(OWNER_GROUP_KEY);
    localStorage.removeItem(GROUP_KEY);
    localStorage.removeItem(TAG_IDS_KEY);
    localStorage.removeItem(APARTMENT_IDS_KEY);
    localStorage.removeItem(SHOW_DECOMMISSIONED_KEY);
};

const initialState: ApartmentFilters = loadFromLocalStorage();

const assign = (state: ApartmentFilters, values: ApartmentFilters): void => {
    state.bedrooms = values.bedrooms;
    state.municipalitySectionId = values.municipalitySectionId;
    state.ownerGroupId = values.ownerGroupId;
    state.groupId = values.groupId;
    state.tagIds = values.tagIds;
    state.apartmentIds = values.apartmentIds;
    state.showDecommissioned = values.showDecommissioned;
};

const apartmentFiltersSlice = createSlice({
    name: "apartmentFilters",
    initialState,
    reducers: {
        setBedrooms: (state, action: PayloadAction<number>) => {
            state.bedrooms = action.payload;
            localStorage.setItem(BEDROOMS_KEY, action.payload.toString());
        },
        setMunicipalitySection: (state, action: PayloadAction<number>) => {
            state.municipalitySectionId = action.payload;
            localStorage.setItem(MUNICIPALITY_KEY, action.payload.toString());
        },
        setOwnerGroup: (state, action: PayloadAction<number>) => {
            state.ownerGroupId = action.payload;
            localStorage.setItem(OWNER_GROUP_KEY, action.payload.toString());
        },
        setGroup: (state, action: PayloadAction<number>) => {
            state.groupId = action.payload;
            localStorage.setItem(GROUP_KEY, action.payload.toString());
        },
        setTagIds: (state, action: PayloadAction<number[]>) => {
            state.tagIds = action.payload;
            localStorage.setItem(TAG_IDS_KEY, JSON.stringify(action.payload));
        },
        setApartmentIds: (state, action: PayloadAction<number[]>) => {
            state.apartmentIds = action.payload;
            localStorage.setItem(APARTMENT_IDS_KEY, JSON.stringify(action.payload));
        },
        setShowDecommissioned: (state, action: PayloadAction<boolean>) => {
            state.showDecommissioned = action.payload;
            localStorage.setItem(SHOW_DECOMMISSIONED_KEY, action.payload ? "true" : "false");
        },
        clearAll: (state) => {
            assign(state, defaultFilters());
            clearPersistedFilters();
        },
        // Same effect as `clearAll`, but the action type is distinct so the
        // intent ("we're logging out") is legible where it's dispatched.
        resetOnLogout: (state) => {
            assign(state, defaultFilters());
            clearPersistedFilters();
        },
        // Re-read the `apartmentFilters.*` localStorage keys and overwrite
        // the in-memory slice with whatever they currently say. Production
        // never dispatches this — the module-level `initialState =
        // loadFromLocalStorage()` runs once at import time, which is the only
        // hydration the live app needs. The action exists so tests that
        // *seed* localStorage before mounting (simulating a browser reload
        // with persisted filters) can force the slice to honour the seed,
        // and so test isolation can reset the slice without permanently
        // wiping persisted state. See `resetAppStore()` in `test/dsl/dsl.tsx`.
        rehydrateFromLocalStorage: (state) => {
            assign(state, loadFromLocalStorage());
        },
    },
});

export const actions = apartmentFiltersSlice.actions;

export default apartmentFiltersSlice.reducer;
